import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, Crown, AlertTriangle, Users, Activity, DollarSign, Eye, Ban, ShieldCheck, Undo2, Ticket, CreditCard, XCircle, PlayCircle, StopCircle, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { confirm } from "../components/ConfirmDialog";

const formatDate = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const AdminTenantDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planDialog, setPlanDialog] = useState({ open: false, plan: "free", expires: "" });
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [impLoading, setImpLoading] = useState(false);
  const [refundDialog, setRefundDialog] = useState({ open: false, tx: null, amountDollars: "", reason: "mischarge", note: "", saving: false });
  const [discountDialog, setDiscountDialog] = useState({ open: false, codeId: "", saving: false });
  const [availableCodes, setAvailableCodes] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [removingDiscount, setRemovingDiscount] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  const fetchData = async () => {
    try {
      const res = await apiClient.get(`/admin/tenants/${tenantId}`);
      setData(res.data);
      setPlanDialog(d => ({ ...d, plan: res.data.plan, expires: res.data.tenant?.plan_expires_at?.split("T")[0] || "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tenant");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tenantId]);

  const updatePlan = async () => {
    try {
      await apiClient.put(`/admin/tenants/${tenantId}/plan`, {
        plan: planDialog.plan,
        plan_expires_at: planDialog.expires ? new Date(planDialog.expires).toISOString() : null
      });
      toast.success(`Plan updated to ${planDialog.plan}`);
      setPlanDialog(d => ({ ...d, open: false }));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleSuspend = async () => {
    try {
      const newState = !data.tenant.suspended;
      await apiClient.post(`/admin/tenants/${tenantId}/suspend`, { suspended: newState });
      toast.success(newState ? "Tenant suspended" : "Tenant reactivated");
      setSuspendDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const impersonate = async (asUserId = null) => {
    setImpLoading(asUserId || "owner");
    const original = localStorage.getItem("session_token");
    try {
      const url = asUserId
        ? `/admin/tenants/${tenantId}/impersonate?as_user_id=${encodeURIComponent(asUserId)}`
        : `/admin/tenants/${tenantId}/impersonate`;
      const res = await apiClient.post(url);
      const newToken = res.data.session_token;
      // Verify the impersonation token actually resolves to a real user before
      // committing — otherwise the app would log the super admin out.
      const verify = await fetch(`${apiClient.defaults.baseURL}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      if (!verify.ok) {
        throw new Error("Impersonation token rejected — tenant may be orphaned.");
      }
      // Stash the original super-admin token so we can switch back
      if (original) localStorage.setItem("admin_original_token", original);
      // Remember where the super admin was — Stop Impersonating returns here
      // instead of dropping them on /admin (they were already looking at a
      // specific tenant, so that's the page they'd want back).
      localStorage.setItem("admin_return_path", window.location.pathname);
      localStorage.setItem("session_token", newToken);
      // Refresh AuthContext with the impersonating user BEFORE navigating.
      // Skipping this triggers Layout.jsx's super-admin guard to bounce the
      // user back to /admin before checkAuth finishes (the "flash and
      // disappear" bug reported in real browsers — Playwright didn't hit it
      // because its timing was faster).
      await checkAuth();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      // Restore the original super-admin token if anything went wrong
      if (original) localStorage.setItem("session_token", original);
      toast.error(e.response?.data?.detail || e.message || "Impersonation failed");
    } finally {
      setImpLoading(false);
    }
  };

  const openRefund = (tx) => {
    // Use the actual charged amount when we know it (populated by the
    // first refund attempt or a future webhook backfill); otherwise fall
    // back to the plan's list price. The backend re-verifies the ceiling
    // via Stripe, so this is just a UX default.
    const listCents = Math.round(Number(tx.amount || 0) * 100);
    const chargedCents = Number(tx.charged_amount_cents) || listCents;
    const remainingCents = chargedCents - Number(tx.refunded_amount_cents || 0);
    setRefundDialog({
      open: true, tx,
      amountDollars: (remainingCents / 100).toFixed(2),
      reason: "mischarge", note: "", saving: false,
    });
  };
  const submitRefund = async () => {
    const { tx, amountDollars, reason, note } = refundDialog;
    if (!tx || !reason) return;
    const requestedCents = Math.round(parseFloat(amountDollars) * 100);
    if (!requestedCents || requestedCents <= 0) {
      toast.error("Enter a valid refund amount.");
      return;
    }
    setRefundDialog(d => ({ ...d, saving: true }));
    try {
      // Compare against actual charged (falls back to list) — matches
      // the backend's cap logic.
      const listCents = Math.round(Number(tx.amount || 0) * 100);
      const chargedCents = Number(tx.charged_amount_cents) || listCents;
      const remaining = chargedCents - Number(tx.refunded_amount_cents || 0);
      const isFull = requestedCents === remaining;
      const body = { reason, note };
      if (!isFull) body.amount_cents = requestedCents;
      await apiClient.post(`/admin/tenants/${tenantId}/transactions/${tx.transaction_id}/refund`, body);
      toast.success("Refund issued via Stripe");
      setRefundDialog({ open: false, tx: null, amountDollars: "", reason: "mischarge", note: "", saving: false });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
      setRefundDialog(d => ({ ...d, saving: false }));
    }
  };

  const syncFromStripe = async () => {
    const ok = await confirm({
      title: "Sync this tenant from Stripe?",
      description: "Read-only reconciler — walks Stripe's recent checkout sessions and updates our LOCAL records: discount badge, subscription ID, transaction rows. It does NOT modify your live Stripe subscription (no double discounts, no re-charges). Safe to run repeatedly — idempotent.",
      confirmLabel: "Sync now",
    });
    if (!ok) return;
    setSyncing(true);
    try {
      const res = await apiClient.post(`/admin/tenants/${tenantId}/sync-from-stripe`);
      const { tenant_updated, transactions_upserted, resolved_subscription_id, applied_discount_backfilled, discount_diagnostic } = res.data;
      const parts = [];
      if (resolved_subscription_id) parts.push(`sub ${resolved_subscription_id.slice(-8)} attached`);
      if (transactions_upserted > 0) parts.push(`${transactions_upserted} tx reconciled`);
      if (applied_discount_backfilled) parts.push(`discount badge ${applied_discount_backfilled} restored`);
      if (parts.length > 0) {
        toast.success(`Synced — ${parts.join(", ")}.`);
      } else if (tenant_updated) {
        toast.success("Synced.");
      } else {
        toast.info("Already in sync — no changes from Stripe.");
      }
      // Surface WHY discount backfill didn't happen — the toast alone isn't
      // enough context when the admin expected a badge but got none. Only
      // show this diagnostic when the sub had a resolved id AND no discount
      // was matched (otherwise it's noise).
      if (resolved_subscription_id && !applied_discount_backfilled && discount_diagnostic) {
        const d = discount_diagnostic;
        let hint;
        if (d.coupon_ids_seen && d.coupon_ids_seen.length > 0) {
          hint = `Found Stripe coupon${d.coupon_ids_seen.length > 1 ? "s" : ""} ${d.coupon_ids_seen.join(", ")} but no matching Nirvanta discount code (check Super Admin → Discounts and make sure the "Stripe Coupon ID" field matches).`;
        } else if (d.error || d.invoice_walk_error) {
          hint = `Discount detection error: ${d.error || d.invoice_walk_error}`;
        } else {
          hint = "No discount code found in this tenant's Stripe sessions, subscription, or invoices.";
        }
        toast.warning(hint, { duration: 8000 });
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const discardPendingTx = async (tx) => {
    const ok = await confirm({
      title: "Discard pending transaction?",
      description: `Mark this ${tx.plan} ${tx.billing_cycle || "monthly"} pending row as abandoned. This is a UI cleanup only — Stripe still auto-expires unpaid sessions after 24h. Use this when you know the session will never complete.`,
      confirmLabel: "Discard",
      variant: "warning",
    });
    if (!ok) return;
    try {
      await apiClient.post(`/admin/tenants/${tenantId}/transactions/${tx.transaction_id}/discard`);
      toast.success("Pending transaction discarded");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to discard");
    }
  };


  const openApplyDiscount = async () => {
    setDiscountDialog({ open: true, codeId: "", saving: false });
    try {
      const res = await apiClient.get("/admin/discount-codes");
      setAvailableCodes((res.data.codes || []).filter(c => c.is_active));
    } catch { setAvailableCodes([]); }
  };
  const submitApplyDiscount = async () => {
    if (!discountDialog.codeId) return;
    setDiscountDialog(d => ({ ...d, saving: true }));
    try {
      const res = await apiClient.post(`/admin/tenants/${tenantId}/apply-discount`, { code_id: discountDialog.codeId });
      toast.success(`Applied "${res.data.code}" to next invoice`);
      setDiscountDialog({ open: false, codeId: "", saving: false });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply discount");
      setDiscountDialog(d => ({ ...d, saving: false }));
    }
  };

  const removeDiscount = async () => {
    const ok = await confirm({
      title: "Remove active discount?",
      description: "The tenant's next invoice will bill at full price. This does NOT refund any past charges — those need a separate refund.",
      confirmText: "Remove discount",
      variant: "warning",
    });
    if (!ok) return;
    setRemovingDiscount(true);
    try {
      await apiClient.post(`/admin/tenants/${tenantId}/remove-discount`);
      toast.success("Discount removed — next invoice will bill at full price");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove discount");
    } finally { setRemovingDiscount(false); }
  };

  const cancelSubscriptionAtPeriodEnd = async () => {
    const ok = await confirm({
      title: "Cancel at end of billing period?",
      description: "The tenant keeps access until their current billing period ends, then Stripe stops future charges. You can resume the subscription any time before that date.",
      confirmText: "Schedule cancellation",
      variant: "warning",
    });
    if (!ok) return;
    setCancelBusy(true);
    try {
      await apiClient.post(`/admin/tenants/${tenantId}/cancel-subscription`, { immediate: false });
      toast.success("Cancellation scheduled for end of period");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule cancellation");
    } finally { setCancelBusy(false); }
  };

  const resumeSubscription = async () => {
    setCancelBusy(true);
    try {
      await apiClient.post(`/admin/tenants/${tenantId}/resume-subscription`);
      toast.success("Subscription resumed — will continue billing normally");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resume subscription");
    } finally { setCancelBusy(false); }
  };

  if (loading) return <AdminLayout><div className="p-10 text-center text-slate-400">Loading…</div></AdminLayout>;
  if (!data) return <AdminLayout><div className="p-10 text-red-500">Tenant not found.</div></AdminLayout>;

  const t = data.tenant || {};
  const u = data.usage || {};
  const limits = data.limits || {};
  const planBadge = t.plan === "pro"
    ? <Badge className="bg-amber-100 text-amber-700"><Crown className="w-3 h-3 mr-1" /> Pro</Badge>
    : t.plan === "starter" ? <Badge className="bg-blue-100 text-blue-700">Starter</Badge>
    : <Badge variant="outline" className="text-slate-500">Free</Badge>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <button onClick={() => navigate("/admin/tenants")} className="text-sm text-slate-500 hover:text-[#0D9488] flex items-center gap-1.5" data-testid="back-to-tenants">
          <ArrowLeft className="w-4 h-4" /> Back to Tenants
        </button>

        {t.suspended && (
          <Card className="border-red-200 bg-red-50/40" data-testid="suspended-banner">
            <CardContent className="p-3 flex items-center gap-2 text-sm">
              <Ban className="w-4 h-4 text-red-500" />
              <span className="font-medium text-red-700">This tenant is suspended.</span>
              <span className="text-red-500/70">Their owners can't sign in until reactivated.</span>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold text-[#0A192F] tracking-tight font-['Manrope'] flex items-center gap-3">
              {t.company_name} {planBadge}
              {t.applied_discount_code && (
                <Badge
                  className="bg-teal-100 text-teal-800 hover:bg-teal-100 font-mono text-[11px]"
                  data-testid="active-discount-badge"
                  title={t.applied_discount_at ? `Applied ${formatDate(t.applied_discount_at)}` : ""}
                >
                  <Ticket className="w-3 h-3 mr-1" /> {t.applied_discount_code}
                </Badge>
              )}
            </h1>
            <p className="text-sm text-slate-500">
              <span className="font-mono">{t.tenant_id}</span> · created {formatDate(t.created_at)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setPlanDialog(d => ({ ...d, open: true }))} data-testid="detail-change-plan-btn">
              <Crown className="w-4 h-4 mr-2" /> Change Plan
            </Button>
            <Button variant="outline" onClick={() => impersonate()} disabled={!!impLoading} className="border-[#0D9488] text-[#0D9488] hover:bg-teal-50" data-testid="impersonate-btn">
              <Eye className="w-4 h-4 mr-2" /> {impLoading === "owner" ? "Loading…" : "Impersonate as Owner"}
            </Button>
            <Button variant="outline" onClick={() => setSuspendDialog(true)} className={t.suspended ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-red-300 text-red-600 hover:bg-red-50"} data-testid="suspend-btn">
              {t.suspended ? <><ShieldCheck className="w-4 h-4 mr-2" /> Reactivate</> : <><Ban className="w-4 h-4 mr-2" /> Suspend</>}
            </Button>
          </div>
        </div>

        {/* Usage stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Active Employees</p>
              <p className="text-2xl font-bold text-[#0A192F]">{u.active_employees}<span className="text-sm text-slate-400 font-normal"> / {limits.max_employees ?? "∞"}</span></p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Time Entries (30d)</p>
              <p className="text-2xl font-bold text-[#0A192F]">{u.time_entries_30d}</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Total Payments</p>
              <p className="text-2xl font-bold text-[#0A192F]">{u.total_payments}</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Last Activity</p>
              <p className="text-sm font-medium text-[#0A192F]">{formatDate(u.last_activity)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Plan details + retention */}
        <Card className="border border-slate-200">
          <CardHeader><CardTitle className="text-base">Plan Limits</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-slate-500">Max Employees</p><p className="font-semibold text-[#0A192F]">{limits.max_employees ?? "∞"}</p></div>
            <div><p className="text-slate-500">Max Locations</p><p className="font-semibold text-[#0A192F]">{limits.max_locations ?? 1}</p></div>
            <div><p className="text-slate-500">Data Retention</p><p className="font-semibold text-[#0A192F]">{limits.data_retention_days ?? "—"} days</p></div>
            <div>
              <p className="text-slate-500">Next Invoice</p>
              <p className="font-semibold text-[#0A192F]">
                {/* Prefer Stripe's upcoming-invoice due_date (the actual
                    next billing date) so this KPI never drifts from the
                    Billing Controls block below. current_period_end is
                    a secondary fallback for older Stripe API responses
                    that didn't include upcoming_invoice, and finally
                    the local plan_expires_at as a last resort. */}
                {data.stripe_subscription?.upcoming_invoice?.due_date
                  ? formatDate(data.stripe_subscription.upcoming_invoice.due_date)
                  : data.stripe_subscription?.current_period_end
                    ? formatDate(data.stripe_subscription.current_period_end)
                    : (t.plan_expires_at ? formatDate(t.plan_expires_at) : "—")}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Billing Controls — one place for every subscription action.
            Always renders (even without a live Stripe sub) so admins have
            a consistent surface. Fallback state guides them to next steps. */}
        <Card className="border border-slate-200" data-testid="billing-controls-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#0D9488]" /> Billing Controls</CardTitle>
              <p className="text-xs text-slate-500 mt-1">All Stripe actions for {t.company_name} live here — numbers below are pulled fresh from Stripe on every page load.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={syncFromStripe}
                disabled={syncing}
                data-testid="billing-controls-sync-btn"
                className="border-[#0D9488]/40 text-[#0D9488] hover:bg-[#0D9488]/10 hover:text-[#0D9488] h-8"
                title="Read-only reconciler — refreshes discount badge, subscription ID, and transactions from Stripe. Never modifies your live subscription."
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync from Stripe"}
              </Button>
              {data.stripe_subscription?.id && (
                <a
                  href={`https://dashboard.stripe.com/subscriptions/${data.stripe_subscription.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#0D9488] hover:underline flex items-center gap-1 px-2"
                  data-testid="open-in-stripe-link"
                >
                  Open in Stripe <ArrowLeft className="w-3 h-3 rotate-[135deg]" />
                </a>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!data.stripe_subscription && (
              <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600" data-testid="no-subscription-state">
                <p className="font-medium text-slate-700 mb-1">No live Stripe subscription for this tenant.</p>
                <p className="text-xs mb-2">
                  {t.plan === "free"
                    ? "This tenant is on the Free plan — no billing to manage. Use Change Plan above to upgrade them or grant a paid plan without charging."
                    : `They have plan "${t.plan}" set locally but Stripe has no active subscription — likely because the initial checkout was never completed OR their subscription was cancelled/refunded.`}
                </p>
                {t.plan !== "free" && (
                  <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded px-2 py-1.5">
                    <span className="font-medium">To apply a discount code:</span> use the <span className="font-semibold">Resend Checkout Link</span> card below — enter the code in the &quot;Discount code&quot; field and it&apos;ll be pre-applied when the owner completes checkout.
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Owner already paid but we didn&apos;t detect it? Use the <span className="font-semibold">↻ Sync from Stripe</span> button in the top-right of this card.
                </p>
              </div>
            )}
            {data.stripe_subscription?.error && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Could not load live Stripe subscription state: {data.stripe_subscription.error}. Refresh to retry.
              </div>
            )}
            {data.stripe_subscription && !data.stripe_subscription.error && (
              <>
                {/* Live subscription snapshot */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-slate-50 rounded-md p-3">
                  <div>
                    <p className="text-slate-500 text-xs">Status</p>
                    <p className="font-semibold text-[#0A192F] capitalize">
                      {data.stripe_subscription.status}
                      {data.stripe_subscription.cancel_at_period_end && (
                        <span className="ml-1 text-amber-600 text-xs">(cancels at period end)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Billing Cycle</p>
                    <p className="font-semibold text-[#0A192F]">
                      ${((data.stripe_subscription.unit_amount_cents || 0) / 100).toFixed(2)} · {data.stripe_subscription.billing_interval === "year" ? "Yearly" : data.stripe_subscription.billing_interval === "month" ? "Monthly" : (data.stripe_subscription.billing_interval || "—")}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">
                      {data.stripe_subscription.cancel_at_period_end ? "Ends On" : "Renews On"}
                    </p>
                    <p className="font-semibold text-[#0A192F]">
                      {/* Prefer upcoming_invoice.due_date — this is the
                          actual date the subscription bills next. Some
                          Stripe API responses don't return a top-level
                          current_period_end but do include it inside the
                          upcoming invoice, so treating them as
                          interchangeable prevents a spurious "—" here. */}
                      {formatDate(
                        data.stripe_subscription.current_period_end
                          || data.stripe_subscription.upcoming_invoice?.due_date
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Active Discount</p>
                    {data.stripe_subscription.discount ? (
                      <p className="font-semibold text-teal-700">
                        {data.stripe_subscription.discount.percent_off
                          ? `${data.stripe_subscription.discount.percent_off}% off`
                          : `$${((data.stripe_subscription.discount.amount_off_cents || 0)/100).toFixed(2)} off`}
                        {data.stripe_subscription.discount.duration === "repeating" && ` × ${data.stripe_subscription.discount.duration_in_months}mo`}
                        {data.stripe_subscription.discount.duration === "once" && " (1 invoice)"}
                        {data.stripe_subscription.discount.duration === "forever" && " (forever)"}
                      </p>
                    ) : (
                      <p className="text-slate-400">None</p>
                    )}
                  </div>
                </div>

                {/* Upcoming invoice preview */}
                {data.stripe_subscription.upcoming_invoice && (
                  <div className="border border-teal-200 bg-teal-50/50 rounded-md p-3" data-testid="upcoming-invoice-preview">
                    <p className="text-xs font-semibold text-teal-800 mb-2 uppercase tracking-wide">Next invoice preview (from Stripe)</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-slate-500 text-xs">Amount Due</p>
                        <p className={`font-bold text-lg ${data.stripe_subscription.upcoming_invoice.amount_due_cents === 0 ? "text-emerald-700" : "text-[#0A192F]"}`}>
                          ${(data.stripe_subscription.upcoming_invoice.amount_due_cents / 100).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Subtotal</p>
                        <p className="font-medium text-slate-700">${(data.stripe_subscription.upcoming_invoice.subtotal_cents / 100).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Discount</p>
                        <p className="font-medium text-teal-700">
                          {data.stripe_subscription.upcoming_invoice.total_discount_cents > 0
                            ? `−$${(data.stripe_subscription.upcoming_invoice.total_discount_cents / 100).toFixed(2)}`
                            : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Bills On</p>
                        <p className="font-medium text-[#0A192F]">{formatDate(data.stripe_subscription.upcoming_invoice.due_date)}</p>
                      </div>
                    </div>
                    {data.stripe_subscription.discount?.duration === "repeating" && data.stripe_subscription.discount.duration_in_months > 1 && (
                      <p className="text-[11px] text-teal-700 mt-2">
                        💡 This discount applies for {data.stripe_subscription.discount.duration_in_months} invoices. After that, the tenant will be billed the full ${((data.stripe_subscription.unit_amount_cents || 0)/100).toFixed(2)}/{data.stripe_subscription.billing_interval}.
                      </p>
                    )}
                    {data.stripe_subscription.discount?.duration === "once" && (
                      <p className="text-[11px] text-teal-700 mt-2">
                        💡 This discount applies to ONLY the next invoice. The invoice after that will bill the full ${((data.stripe_subscription.unit_amount_cents || 0)/100).toFixed(2)}/{data.stripe_subscription.billing_interval}.
                      </p>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={openApplyDiscount} data-testid="apply-discount-btn" className="border-teal-300 text-[#0D9488]">
                    <Ticket className="w-4 h-4 mr-1.5" /> {data.stripe_subscription.discount ? "Change discount" : "Apply discount"}
                  </Button>
                  {data.stripe_subscription.discount && (
                    <Button variant="outline" size="sm" onClick={removeDiscount} disabled={removingDiscount} data-testid="remove-discount-btn" className="border-slate-300 text-slate-600">
                      <XCircle className="w-4 h-4 mr-1.5" /> {removingDiscount ? "Removing…" : "Remove discount"}
                    </Button>
                  )}
                  {data.stripe_subscription.cancel_at_period_end ? (
                    <Button variant="outline" size="sm" onClick={resumeSubscription} disabled={cancelBusy} data-testid="resume-sub-btn" className="border-emerald-300 text-emerald-700">
                      <PlayCircle className="w-4 h-4 mr-1.5" /> Resume subscription
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={cancelSubscriptionAtPeriodEnd} disabled={cancelBusy} data-testid="cancel-sub-btn" className="border-amber-300 text-amber-700">
                      <StopCircle className="w-4 h-4 mr-1.5" /> Cancel at period end
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-400">
                  Refunds are per-transaction — scroll down to Recent Transactions and click Refund on the specific charge.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Convert admin-granted plan → paid subscription, OR resurrect a
            tenant whose Stripe sub was cancelled/refunded but who still
            has a paid plan set locally. Both need a fresh checkout link
            (optionally with a discount code baked in). */}
        {t.plan && t.plan !== "free" && (
          t.plan_source === "admin_grant" || !data.stripe_subscription
        ) && (
          <StartSubscriptionCard
            tenant={t}
            hasLiveSub={!!data.stripe_subscription?.id}
            onLinkGenerated={fetchData}
          />
        )}

        {/* Users */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-base">Users ({data.users?.length || 0})</CardTitle>
            <p className="text-xs text-slate-500">Click <span className="font-medium">Impersonate</span> on any row to debug that user's view — including employees.</p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Last Login</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data.users || []).map(u => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium text-[#0A192F]">{u.name || "—"}</TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDate(u.last_login)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => impersonate(u.user_id)}
                        disabled={!!impLoading}
                        className="h-7 text-[#0D9488] hover:bg-teal-50 hover:text-[#0D9488]"
                        data-testid={`impersonate-user-${u.user_id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />
                        {impLoading === u.user_id ? "Loading…" : "Impersonate"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Transactions */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-base">Recent Transactions ({data.transactions?.length || 0})</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Discount / cancel actions moved to <span className="font-medium">Billing Controls</span> above. Refund a specific charge using the button on that row.</p>
          </CardHeader>
          <CardContent>
            {(data.transactions || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No transactions yet.</p>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Plan</TableHead><TableHead>Cycle</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.transactions.map(tx => {
                    const refunded = Number(tx.refunded_amount_cents || 0);
                    const listCents = Math.round(Number(tx.amount || 0) * 100);
                    const chargedCents = Number(tx.charged_amount_cents) || listCents;
                    const canRefund = ["paid", "partially_refunded"].includes(tx.payment_status) && refunded < chargedCents;
                    const wasDiscounted = chargedCents > 0 && chargedCents < listCents;
                    return (
                      <TableRow key={tx.transaction_id} data-testid={`tx-row-${tx.transaction_id}`} className={tx.payment_status === "abandoned" ? "opacity-50" : ""}>
                        <TableCell className="text-xs text-slate-500">{formatDate(tx.created_at)}</TableCell>
                        <TableCell className="capitalize"><Badge variant="outline">{tx.plan}</Badge></TableCell>
                        <TableCell className="text-xs capitalize text-slate-500">{tx.billing_cycle || "monthly"}</TableCell>
                        <TableCell>
                          <div className="font-medium">${(chargedCents / 100).toFixed(2)}</div>
                          {wasDiscounted && (
                            <div className="text-[10px] text-slate-400">
                              list <span className="line-through">${(listCents / 100).toFixed(2)}</span>
                            </div>
                          )}
                          {refunded > 0 && (
                            <div className="text-[10px] text-rose-600">−${(refunded / 100).toFixed(2)} refunded</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {tx.payment_status === "paid" && <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>}
                          {tx.payment_status === "refunded" && <Badge className="bg-slate-200 text-slate-700">Refunded</Badge>}
                          {tx.payment_status === "partially_refunded" && <Badge className="bg-amber-100 text-amber-700">Partial refund</Badge>}
                          {tx.payment_status === "pending" && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300" title="Stripe checkout started but not completed yet">Pending</Badge>
                          )}
                          {tx.payment_status === "abandoned" && (
                            <Badge variant="outline" className="text-slate-400 border-slate-200" title="User opened Stripe checkout but never paid. Auto-expired after 2h.">Abandoned</Badge>
                          )}
                          {!["paid", "refunded", "partially_refunded", "pending", "abandoned"].includes(tx.payment_status) && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">{tx.payment_status}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {canRefund && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openRefund(tx)}
                              data-testid={`refund-btn-${tx.transaction_id}`}
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            >
                              <Undo2 className="w-4 h-4 mr-1" /> Refund
                            </Button>
                          )}
                          {tx.payment_status === "pending" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => discardPendingTx(tx)}
                              data-testid={`discard-btn-${tx.transaction_id}`}
                              className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                              title="Mark this pending checkout as abandoned"
                            >
                              <X className="w-4 h-4 mr-1" /> Discard
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Plan Dialog */}
      <Dialog open={planDialog.open} onOpenChange={(open) => !open && setPlanDialog(d => ({ ...d, open: false }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Plan</DialogTitle><DialogDescription>Update {t.company_name}'s subscription tier.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={planDialog.plan} onValueChange={(v) => setPlanDialog(d => ({ ...d, plan: v }))}>
                <SelectTrigger data-testid="detail-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free ($0)</SelectItem>
                  <SelectItem value="starter">Starter ($29/mo)</SelectItem>
                  <SelectItem value="pro">Pro ($49/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {planDialog.plan !== "free" && (
              <div className="space-y-2">
                <Label>Expires (blank = permanent)</Label>
                <Input type="date" value={planDialog.expires} onChange={(e) => setPlanDialog(d => ({ ...d, expires: e.target.value }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(d => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="bg-[#0D9488] hover:bg-[#14B8A6]" onClick={updatePlan} data-testid="detail-plan-confirm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog} onOpenChange={setSuspendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> {t.suspended ? "Reactivate" : "Suspend"} this tenant?</DialogTitle>
            <DialogDescription>
              {t.suspended
                ? "Owners and managers will be able to sign in again."
                : "Owners and managers will not be able to sign in. All scheduled jobs (auto-checkout) keep running."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog(false)}>Cancel</Button>
            <Button className={t.suspended ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-red-500 hover:bg-red-600 text-white"} onClick={toggleSuspend} data-testid="confirm-suspend-btn">
              {t.suspended ? "Reactivate" : "Suspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialog.open} onOpenChange={(open) => !open && setRefundDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Undo2 className="w-5 h-5 text-rose-500" /> Refund transaction
            </DialogTitle>
            <DialogDescription>
              {refundDialog.tx && (() => {
                const listCents = Math.round(Number(refundDialog.tx.amount || 0) * 100);
                const chargedCents = Number(refundDialog.tx.charged_amount_cents) || listCents;
                const wasDiscounted = chargedCents > 0 && chargedCents < listCents;
                return (
                  <>
                    Refund the <span className="capitalize font-medium">{refundDialog.tx.plan}</span> charge from {formatDate(refundDialog.tx.created_at)}. Customer paid <span className="font-medium">${(chargedCents / 100).toFixed(2)}</span>{wasDiscounted && <> (list price ${(listCents/100).toFixed(2)}, discounted at checkout)</>}.
                    Refunds process via Stripe and take 5–10 business days to appear on the customer&apos;s card.
                  </>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="refund-amount">Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium pointer-events-none">$</span>
                <Input
                  id="refund-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={refundDialog.amountDollars}
                  onChange={(e) => setRefundDialog(d => ({ ...d, amountDollars: e.target.value }))}
                  className="pl-7"
                  data-testid="refund-amount-input"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Pre-filled with the remaining refundable amount. Edit to issue a partial refund.
                {refundDialog.tx && (() => {
                  const listCents = Math.round(Number(refundDialog.tx.amount || 0) * 100);
                  const chargedCents = Number(refundDialog.tx.charged_amount_cents) || listCents;
                  const remaining = chargedCents - Number(refundDialog.tx.refunded_amount_cents || 0);
                  return ` Max: $${(remaining/100).toFixed(2)}.`;
                })()}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Select value={refundDialog.reason} onValueChange={(v) => setRefundDialog(d => ({ ...d, reason: v }))}>
                <SelectTrigger data-testid="refund-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mischarge">Mischarge — customer was billed incorrectly</SelectItem>
                  <SelectItem value="client_complaint">Client complaint / requested refund</SelectItem>
                  <SelectItem value="duplicate">Duplicate charge</SelectItem>
                  <SelectItem value="fraud">Suspected fraud</SelectItem>
                  <SelectItem value="other">Other (add note below)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="refund-note">Internal note (optional)</Label>
              <Textarea
                id="refund-note"
                rows={3}
                placeholder="Context for the audit trail — not shown to the customer."
                value={refundDialog.note}
                onChange={(e) => setRefundDialog(d => ({ ...d, note: e.target.value }))}
                data-testid="refund-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialog(d => ({ ...d, open: false }))} disabled={refundDialog.saving}>Cancel</Button>
            <Button
              className="bg-rose-500 hover:bg-rose-600 text-white"
              onClick={submitRefund}
              disabled={refundDialog.saving || !refundDialog.amountDollars || Number(refundDialog.amountDollars) <= 0}
              data-testid="refund-confirm-btn"
            >
              {refundDialog.saving ? "Refunding…" : "Refund via Stripe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Discount Dialog */}
      <Dialog open={discountDialog.open} onOpenChange={(open) => !open && setDiscountDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-md" data-testid="apply-discount-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Ticket className="w-5 h-5 text-[#0D9488]" /> Apply discount to next invoice
            </DialogTitle>
            <DialogDescription>
              Attach an existing discount code&apos;s Stripe coupon to <span className="font-medium">{t.company_name}</span>&apos;s active subscription. The discount kicks in on the very next invoice — no re-checkout needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Discount code</Label>
            <Select value={discountDialog.codeId} onValueChange={(v) => setDiscountDialog(d => ({ ...d, codeId: v }))}>
              <SelectTrigger data-testid="apply-discount-code-select"><SelectValue placeholder="Choose a code…" /></SelectTrigger>
              <SelectContent>
                {availableCodes.map(c => (
                  <SelectItem key={c.code_id} value={c.code_id}>
                    <span className="font-mono font-semibold">{c.code}</span> — {c.discount_type === "percent" ? `${c.discount_value}%` : `$${(c.discount_value/100).toFixed(2)}`} off
                    {c.duration_type === "fixed_periods" && ` × ${c.duration_periods}mo`}
                    {c.duration_type === "forever" && ` (recurring)`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableCodes.length === 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                No active discount codes yet. Create one in Admin → Discounts first.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscountDialog(d => ({ ...d, open: false }))} disabled={discountDialog.saving}>Cancel</Button>
            <Button
              className="bg-[#0D9488] hover:bg-[#14B8A6] text-white"
              onClick={submitApplyDiscount}
              disabled={discountDialog.saving || !discountDialog.codeId}
              data-testid="apply-discount-confirm-btn"
            >
              {discountDialog.saving ? "Applying…" : "Apply to next invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// -----------------------------------------------------------------------------
// StartSubscriptionCard — Super Admin action to convert an admin-granted plan
// into a real paid subscription. Generates a Stripe checkout link, optionally
// emails it to the owner, and shows a copy-to-clipboard field so the admin
// can paste it into Slack/SMS/etc.
// -----------------------------------------------------------------------------
const StartSubscriptionCard = ({ tenant, onLinkGenerated, hasLiveSub = false }) => {
  const [plan, setPlan] = useState(tenant.plan === "pro" ? "pro" : "starter");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [discountCode, setDiscountCode] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { url, session_id, owner_email, email_sent }
  const [copied, setCopied] = useState(false);

  // Three intent modes: (a) admin-granted plan → convert to paid;
  // (b) had a Stripe sub but it was cancelled/refunded → resurrect;
  // (c) never completed initial checkout → nudge to finish.
  const isAdminGrant = tenant.plan_source === "admin_grant";
  const cardCopy = isAdminGrant
    ? <>
        <span className="font-medium capitalize">{tenant.company_name}</span> is currently on <span className="font-medium capitalize">{tenant.plan}</span> via an admin grant. Generate a Stripe checkout link so the owner can enter their card and start paying{" — you can attach a discount code below and it will be pre-applied on the Stripe checkout page. "}The plan flips to <span className="font-mono">stripe</span> once payment succeeds.
      </>
    : <>
        <span className="font-medium capitalize">{tenant.company_name}</span> has plan <span className="font-medium capitalize">{tenant.plan}</span> set locally but no live Stripe subscription — likely because the initial checkout was never completed OR their subscription was cancelled/refunded. Generate a fresh checkout link (with an optional discount code baked in) and send it to the owner so they can start paying again.
      </>;

  const [inlineError, setInlineError] = useState(null);
  const navigate = useNavigate();

  const generate = async () => {
    setLoading(true);
    setResult(null);
    setInlineError(null);
    try {
      const res = await apiClient.post(`/admin/tenants/${tenant.tenant_id}/start-subscription`, {
        plan,
        billing_cycle: billingCycle,
        discount_code: discountCode || undefined,
        send_email: sendEmail,
        origin_url: window.location.origin,
      });
      setResult(res.data);
      if (res.data.email_sent) {
        toast.success(`Checkout link emailed to ${res.data.owner_email}`);
      } else {
        toast.success("Checkout link generated");
      }
      onLinkGenerated?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not generate link";
      // Missing-Stripe-price errors need a link, not a toast — surface as
      // an inline callout with a jump-to-Plans-config button.
      if (typeof detail === "string" && detail.startsWith("No Stripe price configured")) {
        setInlineError(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  return (
    <Card className="border border-[#0D9488]/40 bg-[#0D9488]/5" data-testid="start-subscription-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#0D9488]" />
          {isAdminGrant ? "Start Paid Subscription" : "Resend Checkout Link"}
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">{cardCopy}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {inlineError && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 space-y-2"
            data-testid="stripe-price-missing-error"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
              <div className="flex-1 leading-snug">{inlineError}</div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate("/admin/plans")}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="jump-to-plans-config-btn"
            >
              Open Plans config
            </Button>
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Plan</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="startsub-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Billing cycle</Label>
            <Select value={billingCycle} onValueChange={setBillingCycle}>
              <SelectTrigger data-testid="startsub-cycle-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="annual">Annual (save 20%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Discount code (optional)</Label>
            <Input
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
              placeholder="e.g. BLACKFRIDAY20"
              className="font-mono"
              data-testid="startsub-discount-input"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            data-testid="startsub-email-toggle"
          />
          Email the checkout link to the owner
        </label>
        <div className="flex items-center gap-2">
          <Button
            onClick={generate}
            disabled={loading}
            className="bg-[#0D9488] hover:bg-[#14B8A6]"
            data-testid="startsub-generate-btn"
          >
            {loading ? "Generating…" : "Generate Stripe Link"}
          </Button>
        </div>

        {result?.url && (
          <div className="mt-3 space-y-2 rounded-md border border-slate-200 bg-white p-3" data-testid="startsub-result">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Checkout link</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={result.url} className="font-mono text-xs" data-testid="startsub-url-input" />
              <Button size="sm" variant="outline" onClick={copy} data-testid="startsub-copy-btn">
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              {result.owner_email
                ? <>Owner: <span className="font-medium">{result.owner_email}</span>{result.email_sent && <> · <span className="text-emerald-600">✓ Emailed</span></>}</>
                : "No owner email on file — send this link manually."}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


export default AdminTenantDetail;
